import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {fetchFriendRequests, fetchFriends, handleFriendRequest} from "../clients/FriendClient"
import CreateGameModal from "./CreateGameModal";
import UserLink from "./UserLink";
import SendFriendRequestModal from "./SendFriendRequestModal";
import Avatar from "./ui/Avatar";
import Icon from "./ui/Icon";
import {setFriendRequestCount, useNotifications} from "../utils/notificationStore";

const friendsSince = (date) => {
    const value = new Date(date);
    if (Number.isNaN(value.getTime())) return "Friend";
    return `Friends since ${value.toLocaleDateString("en-GB", {month: "short", year: "numeric"})}`;
};

// Friends, friend requests and "add friend" - shown on your own profile
const FriendColumn = () => {
    const navigate = useNavigate();
    const {unreadByFriend} = useNotifications();
    const [friends, setFriends] = useState(null)
    const [friendRequests, setFriendRequests] = useState([])
    const [message, setMessage] = useState(null)
    const [challengeFriend, setChallengeFriend] = useState(null)
    const [showAdd, setShowAdd] = useState(false)

    const loadFriends = () => {
        fetchFriends()
            .then((data) => setFriends([...data].sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.friendName.localeCompare(b.friendName))))
            .catch((error) => {
                setFriends([]);
                setMessage({type: "error", text: error.response?.data?.message || error.message});
            })
    };

    const loadRequests = () => {
        fetchFriendRequests()
            .then((requests) => {
                setFriendRequests(requests);
                setFriendRequestCount(requests.length);
            })
            .catch(() => setFriendRequests([]))
    };

    useEffect(() => {
        loadFriends();
        loadRequests();
        // Refresh who is online every half minute
        const interval = setInterval(loadFriends, 30000);
        return () => clearInterval(interval);
    }, []);

    const answerRequest = (request, accepted) => {
        handleFriendRequest(request.inviterId, accepted)
            .then(() => {
                const remaining = friendRequests.filter((r) => r.inviterId !== request.inviterId);
                setFriendRequests(remaining);
                setFriendRequestCount(remaining.length);
                setMessage({type: "success", text: accepted ? `You and ${request.inviterName} are now friends` : "Request declined"});
                if (accepted) loadFriends();
            })
            .catch((error) => setMessage({type: "error", text: error.response?.data?.message || error.message}))
    };

    const onlineCount = friends?.filter((f) => f.isActive).length || 0;

    return (
        <div className="card">
            <div className="card-header">
                <div>
                    <div className="card-title"><Icon name="users"/> Friends</div>
                    {friends && friends.length > 0 && (
                        <div className="card-subtitle">{onlineCount} online · {friends.length} total</div>
                    )}
                </div>
                <button className={`btn btn-sm ${showAdd ? "" : "btn-secondary"}`} onClick={() => setShowAdd((v) => !v)}
                        aria-expanded={showAdd}>
                    <Icon name={showAdd ? "close" : "userPlus"}/> {showAdd ? "Close" : "Add"}
                </button>
            </div>

            {showAdd && (
                <div className="card-body" style={{borderBottom: "1px solid var(--line)"}}>
                    <SendFriendRequestModal
                        successCallback={(name) => {
                            setMessage({type: "success", text: `Friend request sent to ${name}`});
                            setShowAdd(false);
                        }}
                    />
                </div>
            )}

            {friendRequests.length > 0 && (
                <div className="card-body stack stack--sm" style={{borderBottom: "1px solid var(--line)"}}>
                    <div className="label">Friend requests</div>
                    {friendRequests.map((request) => (
                        <div className="request-card" key={request.inviterId}>
                            <Avatar name={request.inviterName} size={34}/>
                            <div className="request-card__info">
                                <UserLink user={{username: request.inviterName, isActualUser: true}}/>
                                <div className="small muted">wants to be your friend</div>
                            </div>
                            <div className="request-card__actions">
                                <button className="icon-btn icon-btn--sm icon-btn--outlined" style={{color: "var(--leaf-dark)"}}
                                        onClick={() => answerRequest(request, true)} aria-label={`Accept ${request.inviterName}`} title="Accept">
                                    <Icon name="check"/>
                                </button>
                                <button className="icon-btn icon-btn--sm icon-btn--outlined"
                                        onClick={() => answerRequest(request, false)} aria-label={`Decline ${request.inviterName}`} title="Decline">
                                    <Icon name="close"/>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {message && (
                <div className="card-body" style={{paddingBottom: 0}}>
                    <div className={message.type === "error" ? "form-error" : "form-success"}
                         role="status" onClick={() => setMessage(null)}>{message.text}</div>
                </div>
            )}

            {friends === null ? (
                <div className="card-body"><span className="spinner"/></div>
            ) : friends.length === 0 ? (
                <div className="empty-state">
                    <Icon name="sprout"/>
                    <strong>No friends yet</strong>
                    <span className="small">Add someone by their username to chat and challenge them.</span>
                </div>
            ) : (
                <div className="friend-list">
                    {friends.map((friend) => {
                        const unread = unreadByFriend[friend.friendName] || 0;
                        return (
                            <div className="friend-row" key={friend.friendName}>
                                <Avatar name={friend.friendName} size={36} online={!!friend.isActive}/>
                                <div className="friend-row__info">
                                    <UserLink user={{username: friend.friendName, isActualUser: true}}/>
                                    <div className={`friend-row__status ${friend.isActive ? "online" : ""}`}>
                                        {friend.isActive ? "Online now" : friendsSince(friend.friendSince)}
                                    </div>
                                </div>
                                <div className="friend-row__actions">
                                    <button className="icon-btn icon-btn--sm" style={{position: "relative"}}
                                            onClick={() => navigate(`/messages/${encodeURIComponent(friend.friendName)}`)}
                                            aria-label={`Message ${friend.friendName}`} title="Message">
                                        <Icon name="chat"/>
                                        {unread > 0 && <span className="nav-link__badge" style={{position: "absolute", top: -4, right: -4}}>{unread}</span>}
                                    </button>
                                    <button className="icon-btn icon-btn--sm"
                                            onClick={() => setChallengeFriend(friend.friendName)}
                                            disabled={!friend.isActive}
                                            aria-label={`Challenge ${friend.friendName}`}
                                            title={friend.isActive ? "Challenge to a game" : "Your friend is offline"}>
                                        <Icon name="swords"/>
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <CreateGameModal
                showCreateModal={!!challengeFriend}
                closeModal={() => setChallengeFriend(null)}
                friendName={challengeFriend}
            />
        </div>
    )
}

export default FriendColumn;
